import { forwardRef, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Check from '@mui/icons-material/Check';
import { keyframes } from '@mui/material/styles';

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const scaleIn = keyframes`
  0% { opacity: 0; transform: scale(0.5); }
  100% { opacity: 1; transform: scale(1); }
`;

const SUCCESS_HOLD_MS = 1500;

type LoadingButtonProps = Omit<ButtonProps, 'disabled'> & {
  loading: boolean;
  success: boolean;
  loadingText: string;
  successText: string;
};

export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ loading, success, loadingText, successText, children, startIcon, sx, ...rest }, ref) => {
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
      if (success) {
        setShowSuccess(true);
        const timer = setTimeout(() => setShowSuccess(false), SUCCESS_HOLD_MS);
        return () => clearTimeout(timer);
      }
      setShowSuccess(false);
    }, [success]);

    let currentIcon = startIcon;
    if (loading) {
      currentIcon = <CircularProgress size={18} color="inherit" />;
    } else if (showSuccess) {
      currentIcon = <Check sx={{ animation: `${scaleIn} 0.3s ease-out` }} />;
    }

    let currentText: React.ReactNode = children;
    if (loading) {
      currentText = loadingText;
    } else if (showSuccess) {
      currentText = successText;
    }

    return (
      <Button
        ref={ref}
        disabled={loading || showSuccess}
        startIcon={currentIcon}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          transition: 'background-color 0.4s ease, box-shadow 0.4s ease',
          ...(showSuccess && {
            '&.Mui-disabled': {
              bgcolor: 'success.main',
              color: 'success.contrastText',
              boxShadow: (theme) => `0 0 20px ${theme.palette.success.main}40`,
            },
          }),
          ...(loading && {
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: `${shimmer} 1.2s ease-in-out infinite`,
            },
            '&.Mui-disabled': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            },
          }),
          ...sx,
        }}
        {...rest}
      >
        {currentText}
      </Button>
    );
  }
);

LoadingButton.displayName = 'LoadingButton';
